import { Head, router, useForm, usePage } from '@inertiajs/react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ColorPicker } from '@/components/admin/ColorPicker';

type Parish = {
  officialName: string | null;
  shortName: string | null;
  parishType: 'freguesia' | 'uniao' | null;
  district: string | null;
  municipality: string | null;
  contactEmail: string | null;
  mandate: string | null;
  colorPrimary: string;
  colorSecondary: string;
  colorTertiary: string;
  chatEnabled: boolean;
  mailFromName: string | null;
  mailFromEmail: string | null;
  published: boolean;
  logoUrl: string | null;
  faviconUrl: string | null;
  readyToPublish: boolean;
  checklist: { key: string; label: string; done: boolean }[];
};
type Location = {
  id: number;
  name: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  latitude: number | null;
  longitude: number | null;
  openingHours: string | null;
  openingHoursSchedule: { days: string[]; start: string; end: string }[];
  headquarters: boolean;
};
type Venue = { id: number; name: string | null; active: boolean };
type Service = {
  id: number;
  serviceKey: string;
  title: string;
  description: string;
  icon: string;
  enabled: boolean;
};
type Member = {
  id: number;
  body: string;
  name: string | null;
  role: string | null;
  affiliation: string | null;
  photoUrl: string | null;
};
type Party = { key: string; label: string; logoUrl: string };
type Reference = { title: string; source: string; url: string };
type HistoryEntry = {
  id: number;
  era: string | null;
  title: string;
  description: string | null;
  references: Reference[];
  position: number;
};

type Props = {
  parish: Parish;
  locations: Location[];
  venues: Venue[];
  serviceOfferings: Service[];
  bodyMembers: { executive: Member[]; assembly: Member[] };
  parties: Party[];
  roles: { executive: string[]; assembly: string[] };
  historyEntries: HistoryEntry[];
  [key: string]: unknown;
};

const STEPS = [
  { key: 'identity', label: 'Identidade', sub: 'Nome e distrito' },
  { key: 'branding', label: 'Marca', sub: 'Logótipo e cores' },
  { key: 'locations', label: 'Localizações', sub: 'Moradas e horários' },
  { key: 'services', label: 'Serviços', sub: 'Serviços e espaços' },
  { key: 'bodies', label: 'Órgãos', sub: 'Executivo e assembleia', optional: true },
  { key: 'content', label: 'Conteúdo', sub: 'História e páginas', optional: true },
  { key: 'assistant', label: 'Assistente', sub: 'Chat virtual', optional: true },
  { key: 'review', label: 'Rever e publicar', sub: 'Colocar online' },
] as const;

const input =
  'w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-on-surface outline-none focus:border-primary';
const fieldLabel = 'mb-1.5 block text-sm font-medium text-on-surface';
const btnPrimary =
  'rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-50';
const btnGhost =
  'rounded-md border border-line-strong px-4 py-2 text-sm font-medium text-on-surface-light hover:border-primary hover:text-primary';
const card = 'surface-card p-6';

function luminance(hex: string) {
  const c = hex.replace('#', '');
  if (c.length !== 6) return 0;
  const ch = [0, 2, 4].map((i) => {
    const v = parseInt(c.substr(i, 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}
function contrastOnPaper(hex: string) {
  const a = luminance(hex) + 0.05;
  const b = luminance('#f1efe9') + 0.05;
  return Math.max(a, b) / Math.min(a, b);
}

// Debounced autosave: fires `save` ~700ms after the last change, only while dirty.
function useAutosave(isDirty: boolean, data: unknown, save: () => void) {
  const saveRef = useRef(save);
  const dirtyRef = useRef(isDirty);
  useEffect(() => {
    saveRef.current = save;
    dirtyRef.current = isDirty;
  });
  useEffect(() => {
    if (!isDirty) return;
    const t = setTimeout(() => saveRef.current(), 700);
    return () => clearTimeout(t);
  }, [isDirty, data]);
  // Flush a pending edit if the step unmounts (e.g. navigating away quickly).
  useEffect(() => {
    return () => {
      if (dirtyRef.current) saveRef.current();
    };
  }, []);
}

// Compact row action following «Granito & Ouro»: squared, bordered surface,
// colour-only hover, icon + label. `danger` tints the hover for destructive
// actions (remove/delete).
function RowButton({
  icon,
  children,
  onClick,
  tone = 'default',
  className = '',
}: {
  icon: string;
  children?: ReactNode;
  onClick: () => void;
  tone?: 'default' | 'danger';
  className?: string;
}) {
  const toneCls =
    tone === 'danger'
      ? 'border-error/40 text-error hover:border-error hover:bg-error/10'
      : 'border-line-strong text-on-surface-light hover:border-primary hover:text-primary';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${toneCls} ${className}`}
    >
      <span className="material-symbols-outlined text-[15px]">{icon}</span>
      {children}
    </button>
  );
}

// Move `id` order for a reorder request: pull item at `from`, drop it at `to`.
function reorderIds(ids: number[], from: number, to: number): number[] {
  const next = [...ids];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

// Stacked ↑/↓ controls to reorder a list item (disabled at the ends).
function ReorderButtons({
  onUp,
  onDown,
  canUp,
  canDown,
}: {
  onUp: () => void;
  onDown: () => void;
  canUp: boolean;
  canDown: boolean;
}) {
  const btn =
    'text-on-surface-light hover:text-primary flex disabled:cursor-default disabled:opacity-30 disabled:hover:text-on-surface-light';
  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-label="Mover para cima"
        disabled={!canUp}
        onClick={onUp}
        className={btn}
      >
        <span className="material-symbols-outlined text-[18px]">keyboard_arrow_up</span>
      </button>
      <button
        type="button"
        aria-label="Mover para baixo"
        disabled={!canDown}
        onClick={onDown}
        className={btn}
      >
        <span className="material-symbols-outlined text-[18px]">keyboard_arrow_down</span>
      </button>
    </div>
  );
}

// «Granito & Ouro» on/off switch — the active track uses the parish's own
// primary colour (runtime-themed). Replaces the raw OS checkbox.
function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`focus-visible:ring-primary/40 relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none ${
        checked ? 'bg-primary' : 'bg-line-strong'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

type SelectOption = { value: string; label: string; iconUrl?: string };

// Accessible custom dropdown (button + own listbox) — unlike a native
// <select>, the OPEN list is rendered by us so it follows «Granito & Ouro».
// Implements the WAI-ARIA collapsible-listbox pattern: combobox button with
// aria-expanded/aria-activedescendant + a role="listbox" of role="option"s,
// fully keyboard-operable (↑/↓/Home/End/Enter/Esc). `noneLabel`, when set,
// prepends a clearing option (value ''). Options may carry an iconUrl.
function SelectMenu({
  value,
  options,
  onChange,
  placeholder,
  ariaLabel,
  noneLabel,
}: {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  noneLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const baseId = useId();

  const items: SelectOption[] = noneLabel ? [{ value: '', label: noneLabel }, ...options] : options;
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open && activeIndex >= 0) {
      listRef.current
        ?.querySelector(`[data-idx="${activeIndex}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIndex]);

  const openMenu = () => {
    const idx = items.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
  };
  const choose = (v: string) => {
    onChange(v);
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(items.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(items.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (activeIndex >= 0) choose(items[activeIndex].value);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Tab':
        close();
        break;
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${baseId}-list`}
        aria-activedescendant={
          open && activeIndex >= 0 ? `${baseId}-opt-${activeIndex}` : undefined
        }
        aria-label={ariaLabel}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onKeyDown}
        className="border-line bg-surface hover:border-primary text-on-surface focus:border-primary focus-visible:ring-primary/30 flex w-full items-center gap-2 rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2"
      >
        {selected?.iconUrl && <img src={selected.iconUrl} alt="" className="h-5 w-5 rounded" />}
        <span className={selected ? 'truncate' : 'text-on-surface-light truncate'}>
          {selected ? selected.label : (placeholder ?? 'Selecionar…')}
        </span>
        <span className="material-symbols-outlined text-on-surface-light ml-auto text-[18px]">
          expand_more
        </span>
      </button>
      {open && (
        <div
          ref={listRef}
          id={`${baseId}-list`}
          role="listbox"
          aria-label={ariaLabel}
          className="border-line bg-surface no-scrollbar absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border shadow-xl"
        >
          {items.map((o, i) => (
            // Keyboard is handled on the combobox via aria-activedescendant, so
            // options are pointer targets only (tabIndex -1, no own key handler).
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              key={o.value || '__none'}
              id={`${baseId}-opt-${i}`}
              data-idx={i}
              role="option"
              tabIndex={-1}
              aria-selected={o.value === value}
              onClick={() => choose(o.value)}
              onMouseEnter={() => setActiveIndex(i)}
              className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm ${
                i === activeIndex ? 'bg-background' : ''
              } ${o.value === value ? 'bg-primary/5 font-semibold' : ''}`}
            >
              {o.iconUrl && <img src={o.iconUrl} alt="" className="h-5 w-5 rounded" />}
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AutosaveStatus({
  processing,
  saved,
  failed,
}: {
  processing: boolean;
  saved: boolean;
  failed?: boolean;
}) {
  return (
    <span className="text-on-surface-light flex items-center gap-1.5 text-xs" aria-live="polite">
      {processing ? (
        <>
          <span className="border-primary/40 border-t-primary h-3 w-3 animate-spin rounded-full border-2" />
          A guardar…
        </>
      ) : failed ? (
        <span className="text-error flex items-center gap-1.5">
          <span className="material-symbols-outlined text-sm">error</span>
          Não foi possível guardar — verifique os campos
        </span>
      ) : saved ? (
        <>
          <span className="text-tertiary material-symbols-outlined text-sm">check</span>
          Guardado
        </>
      ) : (
        <span className="text-on-surface-light/60">Guarda automaticamente</span>
      )}
    </span>
  );
}

export default function Onboarding() {
  const {
    parish,
    locations,
    venues,
    serviceOfferings,
    bodyMembers,
    parties,
    roles,
    historyEntries,
  } = usePage<Props>().props;
  const [started, setStarted] = useState(parish.published);
  const [step, setStep] = useState(0);

  const doneKeys = new Set(parish.checklist.filter((c) => c.done).map((c) => c.key));
  // Whether each step has content — required steps use the publish checklist,
  // optional steps reflect whatever the parish has filled in.
  const stepDone = (key: string) => {
    switch (key) {
      case 'identity':
        return doneKeys.has('identity') && doneKeys.has('contact');
      case 'branding':
        return doneKeys.has('branding');
      case 'locations':
        return doneKeys.has('location');
      case 'services':
        return serviceOfferings.some((s) => s.enabled);
      case 'bodies':
        return bodyMembers.executive.length + bodyMembers.assembly.length > 0;
      case 'content':
        return historyEntries.length > 0;
      case 'assistant':
        return parish.chatEnabled;
      case 'review':
        return parish.published;
      default:
        return false;
    }
  };

  if (!started) {
    return (
      <>
        <Head title="Configuração — Administração" />
        <Welcome onStart={() => setStarted(true)} />
      </>
    );
  }

  const current = STEPS[step];

  return (
    <>
      <Head title="Configuração — Administração" />
      <div className="mb-6">
        <h1 className="font-headline text-on-surface text-2xl font-bold">Configuração do site</h1>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        <nav className="surface-card h-fit overflow-hidden p-0" aria-label="Passos">
          {STEPS.map((s, i) => {
            const done = stepDone(s.key);
            return (
              <button
                key={s.key}
                onClick={() => setStep(i)}
                aria-current={i === step}
                className={`flex w-full items-center gap-3 border-l-[3px] px-4 py-3 text-left ${
                  i === step
                    ? 'border-primary bg-primary/5 text-on-surface font-semibold'
                    : 'text-on-surface-light hover:bg-primary/[0.03] border-transparent'
                }`}
              >
                <span
                  className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold ${
                    done
                      ? 'bg-tertiary text-white'
                      : i === step
                        ? 'border-primary text-primary border'
                        : 'border-line-strong border'
                  }`}
                >
                  {done ? (
                    <span className="material-symbols-outlined text-[15px]">check</span>
                  ) : (
                    i + 1
                  )}
                </span>
                <span className="text-sm">
                  {s.label}
                  {'optional' in s && s.optional && (
                    <span className="text-secondary bg-secondary/10 ml-1.5 rounded px-1 py-0.5 text-[9px] font-bold tracking-wide uppercase">
                      Opcional
                    </span>
                  )}
                  <span className="text-on-surface-light block text-[11px] font-normal">
                    {s.sub}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>

        <section>
          {current.key === 'identity' && <IdentityStep parish={parish} />}
          {current.key === 'branding' && <BrandingStep parish={parish} />}
          {current.key === 'locations' && <LocationsStep locations={locations} />}
          {current.key === 'services' && (
            <ServicesStep services={serviceOfferings} venues={venues} />
          )}
          {current.key === 'bodies' && (
            <BodiesStep members={bodyMembers} parties={parties} roles={roles} parish={parish} />
          )}
          {current.key === 'content' && <ContentStep historyEntries={historyEntries} />}
          {current.key === 'assistant' && <AssistantStep parish={parish} />}
          {current.key === 'review' && (
            <ReviewStep
              parish={parish}
              locations={locations}
              services={serviceOfferings}
              bodyMembers={bodyMembers}
              historyEntries={historyEntries}
            />
          )}

          <div className="mt-6 flex items-center justify-between">
            <button
              className={btnGhost}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
            >
              ← Anterior
            </button>
            <span className="text-on-surface-light text-xs">
              Passo {step + 1} de {STEPS.length}
            </span>
            <button
              className={btnPrimary}
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={step === STEPS.length - 1}
            >
              Seguinte →
            </button>
          </div>
        </section>
      </div>
    </>
  );
}

function Welcome({ onStart }: { onStart: () => void }) {
  return (
    <div className="mx-auto max-w-xl">
      <div className={`${card} text-center`}>
        <div className="page-eyebrow text-secondary text-xs font-bold tracking-widest uppercase">
          Configuração inicial
        </div>
        <h1 className="font-headline text-on-surface mt-2 text-3xl font-semibold">Bem-vindo 👋</h1>
        <p className="text-on-surface-light mx-auto mt-3 max-w-md">
          Vamos preparar o site da sua freguesia. São oito passos simples — pode guardar e continuar
          mais tarde. O site público só fica visível quando publicar.
        </p>
        <div className="text-on-surface-light mt-4 text-sm">⏱️ Cerca de 10 minutos</div>
        <div className="bg-background border-line mt-6 rounded-xl border p-5 text-left">
          <p className="text-secondary mb-3 text-xs font-bold tracking-widest uppercase">
            Tenha à mão
          </p>
          <ul className="text-on-surface-light flex flex-col gap-2 text-sm">
            <li>🖼️ O logótipo/brasão (PNG ou SVG)</li>
            <li>🎨 As cores heráldicas</li>
            <li>📍 Moradas, telefones e horários dos balcões</li>
            <li>👥 Os membros dos órgãos (opcional)</li>
          </ul>
        </div>
        <button className={`${btnPrimary} mt-6`} onClick={onStart}>
          Começar →
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className={fieldLabel}>{label}</span>
      {hint && <span className="text-on-surface-light mb-1.5 block text-xs">{hint}</span>}
      {children}
      {error && (
        <span className="text-error mt-1 flex items-center gap-1 text-xs" role="alert">
          <span className="material-symbols-outlined text-[13px]">error</span>
          {error}
        </span>
      )}
    </label>
  );
}

// Inertia's Rails adapter returns each field's errors as a string (or an
// array of strings). Normalise to the first human-readable message.
type FormError = string | string[] | undefined;
function errText(e: FormError): string | undefined {
  return Array.isArray(e) ? e[0] : e;
}

// Compact summary of server-side validation errors for the explicit-save
// (add/edit) forms, shown next to the action buttons.
function FormErrors({ errors }: { errors: Record<string, FormError> }) {
  const messages = Object.values(errors)
    .map(errText)
    .filter((m): m is string => Boolean(m));
  if (messages.length === 0) return null;
  return (
    <div className="text-error mt-2 flex items-start gap-1.5 text-xs" role="alert">
      <span className="material-symbols-outlined text-sm">error</span>
      <span>{messages.join(' · ')}</span>
    </div>
  );
}

function StepHeader({ title, help }: { title: string; help: string }) {
  return (
    <div className="mb-6">
      <h2 className="font-headline text-on-surface text-xl font-semibold">{title}</h2>
      <p className="text-on-surface-light mt-1.5 text-sm">{help}</p>
    </div>
  );
}

function IdentityStep({ parish }: { parish: Parish }) {
  const form = useForm({
    official_name: parish.officialName ?? '',
    short_name: parish.shortName ?? '',
    parish_type: parish.parishType ?? 'uniao',
    district: parish.district ?? '',
    municipality: parish.municipality ?? '',
    contact_email: parish.contactEmail ?? '',
  });
  const save = () => {
    form.transform((d) => ({ parish: d }));
    form.patch('/parish', {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => form.setDefaults(),
    });
  };
  useAutosave(form.isDirty, form.data, save);
  return (
    <div className={card}>
      <StepHeader
        title="Identidade da freguesia"
        help="O nome oficial e o distrito aparecem no cabeçalho, no rodapé e nos emails."
      />
      <Field
        label="Nome oficial"
        hint="Designação legal completa — usada no rodapé, no título das páginas e nos emails. Ex.: «União das Freguesias de … (…)» ou «Freguesia de …»"
      >
        <input
          className={input}
          value={form.data.official_name}
          onChange={(e) => form.setData('official_name', e.target.value)}
        />
      </Field>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Nome curto"
          hint="Versão compacta para espaços apertados: cabeçalho, assistente. Ex.: «Santa Maria»"
        >
          <input
            className={input}
            value={form.data.short_name}
            onChange={(e) => form.setData('short_name', e.target.value)}
          />
        </Field>
        <Field label="Tipo">
          <SelectMenu
            value={form.data.parish_type ?? 'uniao'}
            options={[
              { value: 'uniao', label: 'União de Freguesias' },
              { value: 'freguesia', label: 'Freguesia' },
            ]}
            onChange={(v) => form.setData('parish_type', v as 'freguesia' | 'uniao')}
            ariaLabel="Tipo"
          />
        </Field>
        <Field label="Distrito">
          <input
            className={input}
            value={form.data.district}
            onChange={(e) => form.setData('district', e.target.value)}
          />
        </Field>
        <Field
          label="Concelho"
          hint="Município a que a freguesia pertence. Ex.: «Braga». Usado, p. ex., na Câmara Municipal referida na Política de Privacidade."
        >
          <input
            className={input}
            value={form.data.municipality}
            onChange={(e) => form.setData('municipality', e.target.value)}
          />
        </Field>
        <Field label="Email de contacto" error={errText(form.errors.contact_email)}>
          <input
            type="email"
            className={input}
            value={form.data.contact_email}
            onChange={(e) => form.setData('contact_email', e.target.value)}
          />
        </Field>
      </div>
      <div className="mt-6">
        <AutosaveStatus
          processing={form.processing}
          saved={form.recentlySuccessful}
          failed={form.hasErrors}
        />
      </div>
    </div>
  );
}

// Image attachment field (logo / favicon). Uploads immediately on selection
// as its own multipart PATCH to /parish; previews the picked file meanwhile.
function ImageUpload({
  field,
  currentUrl,
  label,
  hint,
}: {
  field: 'logo' | 'favicon';
  currentUrl: string | null;
  label: string;
  hint: string;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);
  const upload = (f: File) => {
    setFile(f);
    setUploading(true);
    router.patch(
      '/parish',
      { [field]: f },
      {
        forceFormData: true,
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
          setUploading(false);
          setFile(null);
        },
      },
    );
  };
  return (
    <div>
      <span className={fieldLabel}>{label}</span>
      <div className="mb-3 flex items-center gap-3">
        <div className="border-line bg-background flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border">
          {currentUrl ? (
            <img src={currentUrl} alt={label} className="max-h-14 w-auto" />
          ) : (
            <span className="material-symbols-outlined text-on-surface-light/50 text-2xl">
              image
            </span>
          )}
        </div>
        <div className="text-sm">
          <div className="font-medium">{label} atual</div>
          <div className="text-on-surface-light text-xs">
            {currentUrl ? 'Carregado' : 'Ainda sem imagem'}
          </div>
        </div>
      </div>
      <label className="border-line-strong hover:border-primary bg-background flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors">
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload(f);
          }}
        />
        {preview ? (
          <img src={preview} alt="Pré-visualização" className="max-h-20 w-auto" />
        ) : (
          <span className="material-symbols-outlined text-on-surface-light text-3xl">
            cloud_upload
          </span>
        )}
        <span className="text-primary text-sm font-semibold">
          {uploading
            ? 'A carregar…'
            : currentUrl
              ? `Alterar ${label.toLowerCase()}`
              : `Carregar ${label.toLowerCase()}`}
        </span>
        <span className="text-on-surface-light text-xs">{hint}</span>
      </label>
    </div>
  );
}

function BrandingStep({ parish }: { parish: Parish }) {
  const form = useForm({
    color_primary: parish.colorPrimary,
    color_secondary: parish.colorSecondary,
    color_tertiary: parish.colorTertiary,
  });
  const save = () => {
    form.transform((d) => ({ parish: d }));
    form.patch('/parish', {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => form.setDefaults(),
    });
  };
  useAutosave(form.isDirty, form.data, save);
  const swatches: [keyof typeof form.data, string][] = [
    ['color_primary', 'Cor primária'],
    ['color_secondary', 'Cor secundária'],
    ['color_tertiary', 'Cor terciária'],
  ];

  return (
    <div className={card}>
      <StepHeader
        title="Logótipo e cores"
        help="Escolha as três cores heráldicas. Derivamos automaticamente variantes legíveis para texto."
      />
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <ImageUpload
              field="logo"
              currentUrl={parish.logoUrl}
              label="Logótipo"
              hint="PNG, SVG ou JPG · fundo transparente"
            />
            <ImageUpload
              field="favicon"
              currentUrl={parish.faviconUrl}
              label="Favicon"
              hint="Ícone do separador do browser · PNG quadrado"
            />
          </div>

          <div className="mt-6 flex flex-col gap-3">
            {swatches.map(([key, label]) => {
              const value = form.data[key];
              const ok = contrastOnPaper(value) >= 4.5;
              return (
                <div key={key} className="flex items-center gap-3">
                  <ColorPicker value={value} onChange={(hex) => form.setData(key, hex)} />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{label}</div>
                    <div
                      className={`flex items-center gap-1 text-[11px] ${ok ? 'text-tertiary' : 'text-error'}`}
                    >
                      <span className="material-symbols-outlined text-[13px]">
                        {ok ? 'check_circle' : 'warning'}
                      </span>
                      {ok
                        ? 'Contraste adequado'
                        : 'Clara para texto — usaremos variante mais escura'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div className="text-on-surface-light mb-2 text-xs font-bold tracking-widest uppercase">
            Pré-visualização
          </div>
          <div
            className="border-line overflow-hidden rounded-xl border"
            style={
              {
                '--color-primary': form.data.color_primary,
                '--color-secondary': form.data.color_secondary,
                '--color-tertiary': form.data.color_tertiary,
              } as React.CSSProperties
            }
          >
            <div className="bg-on-surface px-4 py-3 text-white">
              <div className="text-secondary text-[10px] font-bold tracking-widest uppercase">
                {parish.parishType === 'uniao' ? 'União das Freguesias' : 'Freguesia'}
              </div>
              <div className="font-headline text-sm">{parish.shortName || 'A sua freguesia'}</div>
            </div>
            <div
              className="h-1"
              style={{
                background:
                  'linear-gradient(90deg, var(--color-primary) 0 33.33%, var(--color-secondary) 33.33% 66.66%, var(--color-tertiary) 66.66% 100%)',
              }}
            />
            <div className="bg-background p-4">
              <span
                className="inline-block rounded-md px-3 py-1.5 text-xs font-semibold text-white"
                style={{ background: 'var(--color-primary)' }}
              >
                Pedir atestado
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <AutosaveStatus
          processing={form.processing}
          saved={form.recentlySuccessful}
          failed={form.hasErrors}
        />
      </div>
    </div>
  );
}

type ScheduleGroup = { days: string[]; start: string; end: string };
const WEEK_DAYS: [string, string][] = [
  ['seg', 'Seg'],
  ['ter', 'Ter'],
  ['qua', 'Qua'],
  ['qui', 'Qui'],
  ['sex', 'Sex'],
  ['sab', 'Sáb'],
  ['dom', 'Dom'],
];
const timeInput =
  'border-line bg-surface focus:border-primary rounded-md border px-2 py-1.5 text-sm outline-none';

function ScheduleEditor({
  value,
  onChange,
}: {
  value: ScheduleGroup[];
  onChange: (groups: ScheduleGroup[]) => void;
}) {
  const patch = (i: number, next: Partial<ScheduleGroup>) =>
    onChange(value.map((g, idx) => (idx === i ? { ...g, ...next } : g)));
  const toggleDay = (i: number, day: string) => {
    const days = value[i].days.includes(day)
      ? value[i].days.filter((d) => d !== day)
      : [...value[i].days, day];
    patch(i, { days });
  };

  return (
    <div className="sm:col-span-2">
      <span className={fieldLabel}>Horário de atendimento</span>
      <p className="text-on-surface-light mb-2 text-xs">
        Selecione os dias e o intervalo de horas de cada período de atendimento deste balcão.
      </p>
      <div className="flex flex-col gap-3">
        {value.map((g, i) => (
          <div key={i} className="border-line rounded-lg border p-3">
            <div className="mb-3 flex flex-wrap gap-1.5">
              {WEEK_DAYS.map(([d, lbl]) => (
                <button
                  key={d}
                  type="button"
                  aria-pressed={g.days.includes(d)}
                  onClick={() => toggleDay(i, d)}
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                    g.days.includes(d)
                      ? 'bg-primary text-white'
                      : 'border-line text-on-surface-light bg-background border'
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="time"
                aria-label="Hora de início"
                className={timeInput}
                value={g.start}
                onChange={(e) => patch(i, { start: e.target.value })}
              />
              <span className="text-on-surface-light">–</span>
              <input
                type="time"
                aria-label="Hora de fim"
                className={timeInput}
                value={g.end}
                onChange={(e) => patch(i, { end: e.target.value })}
              />
              <RowButton
                icon="delete"
                tone="danger"
                className="ml-auto"
                onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              >
                Remover
              </RowButton>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([...value, { days: [], start: '09:00', end: '17:00' }])}
        className={`${btnGhost} mt-2 inline-flex items-center gap-1.5`}
      >
        <span className="material-symbols-outlined text-[16px]">add</span>
        Adicionar período
      </button>
    </div>
  );
}

function LocationForm({ location, onDone }: { location?: Location; onDone?: () => void }) {
  const [schedule, setSchedule] = useState<ScheduleGroup[]>(location?.openingHoursSchedule ?? []);
  const form = useForm({
    name: location?.name ?? '',
    address: location?.address ?? '',
    phone: location?.phone ?? '',
    email: location?.email ?? '',
    latitude: location?.latitude != null ? String(location.latitude) : '',
    longitude: location?.longitude != null ? String(location.longitude) : '',
    opening_hours_schedule: JSON.stringify(location?.openingHoursSchedule ?? []),
    headquarters: location?.headquarters ?? false,
  });
  const updateSchedule = (groups: ScheduleGroup[]) => {
    setSchedule(groups);
    form.setData('opening_hours_schedule', JSON.stringify(groups));
  };
  const submit = () => {
    form.transform((d) => ({ parish_location: d }));
    if (location) {
      form.patch(`/locations/${location.id}`, { preserveScroll: true, onSuccess: onDone });
    } else {
      form.post('/locations', {
        preserveScroll: true,
        onSuccess: () => {
          form.reset();
          setSchedule([]);
        },
      });
    }
  };
  return (
    <div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          className={input}
          placeholder="Nome (ex: sede principal)"
          value={form.data.name}
          onChange={(e) => form.setData('name', e.target.value)}
        />
        <input
          className={input}
          placeholder="Telefone"
          value={form.data.phone}
          onChange={(e) => form.setData('phone', e.target.value)}
        />
        <input
          className={`${input} sm:col-span-2`}
          placeholder="Morada completa"
          value={form.data.address}
          onChange={(e) => form.setData('address', e.target.value)}
        />
        <input
          className={input}
          placeholder="Email (opcional)"
          value={form.data.email}
          onChange={(e) => form.setData('email', e.target.value)}
        />
        <div className="sm:col-span-2">
          <p className="text-on-surface-light mb-1.5 text-xs">
            Coordenadas para o mapa (opcional). No Google Maps, clique com o botão direito no local
            e copie os dois números que aparecem no topo.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <input
              className={input}
              inputMode="decimal"
              placeholder="Latitude (ex: 41.496)"
              value={form.data.latitude}
              onChange={(e) => form.setData('latitude', e.target.value)}
              aria-label="Latitude"
            />
            <input
              className={input}
              inputMode="decimal"
              placeholder="Longitude (ex: -8.408)"
              value={form.data.longitude}
              onChange={(e) => form.setData('longitude', e.target.value)}
              aria-label="Longitude"
            />
          </div>
        </div>
        <ScheduleEditor value={schedule} onChange={updateSchedule} />
        <div className="border-line flex items-center justify-between rounded-lg border p-3 sm:col-span-2">
          <div>
            <div className="text-on-surface text-sm font-semibold">É a sede</div>
            <div className="text-on-surface-light text-xs">O balcão principal da freguesia</div>
          </div>
          <Switch
            checked={form.data.headquarters}
            onChange={(v) => form.setData('headquarters', v)}
            label="Definir como sede (balcão principal)"
          />
        </div>
      </div>
      <div className="mt-4 flex gap-3">
        <button
          className={btnPrimary}
          onClick={submit}
          disabled={form.processing || !form.data.name}
        >
          {location ? 'Guardar' : 'Adicionar localização'}
        </button>
        {location && (
          <button className={btnGhost} onClick={onDone}>
            Cancelar
          </button>
        )}
      </div>
      <FormErrors errors={form.errors} />
    </div>
  );
}

function LocationRow({
  location,
  onUp,
  onDown,
  canUp,
  canDown,
}: {
  location: Location;
  onUp: () => void;
  onDown: () => void;
  canUp: boolean;
  canDown: boolean;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <div className="border-line bg-background rounded-lg border p-4">
        <LocationForm location={location} onDone={() => setEditing(false)} />
      </div>
    );
  }
  return (
    <div className="border-line flex items-start justify-between gap-3 rounded-lg border p-4">
      <div className="flex min-w-0 items-start gap-2">
        <ReorderButtons onUp={onUp} onDown={onDown} canUp={canUp} canDown={canDown} />
        <div className="min-w-0">
          <div className="text-sm font-semibold">
            {location.name}{' '}
            {location.headquarters && <span className="text-secondary text-xs">· Sede</span>}
          </div>
          <div className="text-on-surface-light text-sm">{location.address}</div>
          <div className="text-on-surface-light text-xs">{location.phone}</div>
          {location.openingHours && (
            <div className="text-on-surface-light mt-1 text-xs whitespace-pre-line">
              {location.openingHours}
            </div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <RowButton icon="edit" onClick={() => setEditing(true)}>
          Editar
        </RowButton>
        <RowButton
          icon="delete"
          tone="danger"
          onClick={() => router.delete(`/locations/${location.id}`, { preserveScroll: true })}
        >
          Remover
        </RowButton>
      </div>
    </div>
  );
}

function LocationsStep({ locations }: { locations: Location[] }) {
  const move = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= locations.length) return;
    const ids = reorderIds(
      locations.map((l) => l.id),
      index,
      to,
    );
    router.patch('/locations/reorder', { ids }, { preserveScroll: true, preserveState: true });
  };
  return (
    <div className={card}>
      <StepHeader
        title="Balcões e moradas"
        help="Adicione cada balcão de atendimento. A primeira ou a marcada como sede é a principal."
      />
      <div className="mb-6 flex flex-col gap-3">
        {locations.map((l, i) => (
          <LocationRow
            key={l.id}
            location={l}
            onUp={() => move(i, -1)}
            onDown={() => move(i, 1)}
            canUp={i > 0}
            canDown={i < locations.length - 1}
          />
        ))}
        {locations.length === 0 && (
          <p className="text-on-surface-light text-sm">Ainda não há localizações.</p>
        )}
      </div>
      <div className="border-line border-t pt-5">
        <LocationForm />
      </div>
    </div>
  );
}

function VenueRow({ venue }: { venue: Venue }) {
  const [editing, setEditing] = useState(false);
  const form = useForm({ name: venue.name ?? '' });
  const save = () => {
    form.transform((d) => ({ venue: d }));
    form.patch(`/venues/${venue.id}`, { preserveScroll: true, onSuccess: () => setEditing(false) });
  };
  if (editing) {
    return (
      <div className="flex w-full items-center gap-2">
        <input
          className={input}
          value={form.data.name}
          onChange={(e) => form.setData('name', e.target.value)}
          aria-label="Nome do espaço"
        />
        <button className={btnPrimary} onClick={save} disabled={!form.data.name}>
          Guardar
        </button>
        <button className={btnGhost} onClick={() => setEditing(false)}>
          Cancelar
        </button>
      </div>
    );
  }
  return (
    <span className="border-line bg-background inline-flex items-center gap-2.5 rounded-md border px-3.5 py-2 text-sm">
      {venue.name}
      <button
        className="text-on-surface-light hover:text-primary flex"
        aria-label="Editar espaço"
        onClick={() => setEditing(true)}
      >
        <span className="material-symbols-outlined text-[12px]">edit</span>
      </button>
      <button
        className="text-error/70 hover:text-error flex"
        aria-label="Remover espaço"
        onClick={() => router.delete(`/venues/${venue.id}`, { preserveScroll: true })}
      >
        <span className="material-symbols-outlined text-[12px]">delete</span>
      </button>
    </span>
  );
}

function ServicesStep({ services, venues }: { services: Service[]; venues: Venue[] }) {
  const venueForm = useForm({ name: '' });
  const toggle = (s: Service) =>
    router.patch(
      `/service-offerings/${s.id}`,
      { service_offering: { enabled: !s.enabled } },
      { preserveScroll: true },
    );
  const addVenue = () => {
    venueForm.transform((d) => ({ venue: d }));
    venueForm.post('/venues', { preserveScroll: true, onSuccess: () => venueForm.reset() });
  };
  return (
    <div className={card}>
      <StepHeader
        title="Que serviços oferece?"
        help="Ative apenas os serviços que a freguesia disponibiliza. Só aparecem os que ativar."
      />
      <div className="flex flex-col gap-2">
        {services.map((s) => (
          <div
            key={s.id}
            className="border-line flex items-center justify-between gap-3 rounded-lg border p-3"
          >
            <div className="flex items-center gap-3">
              <span
                className="bg-background border-line flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border text-lg"
                aria-hidden="true"
              >
                {s.icon}
              </span>
              <div>
                <div className="text-sm font-semibold">{s.title}</div>
                <div className="text-on-surface-light text-xs">{s.description}</div>
              </div>
            </div>
            <Switch checked={s.enabled} onChange={() => toggle(s)} label={s.title} />
          </div>
        ))}
      </div>

      <div className="border-line mt-6 border-t pt-5">
        <p className={fieldLabel}>Espaços reserváveis</p>
        <div className="mb-3 flex flex-wrap gap-2">
          {venues.map((v) => (
            <VenueRow key={v.id} venue={v} />
          ))}
        </div>
        <div className="flex gap-2">
          <input
            className={input}
            placeholder="Nome do espaço"
            value={venueForm.data.name}
            onChange={(e) => venueForm.setData('name', e.target.value)}
          />
          <button className={btnGhost} onClick={addVenue} disabled={!venueForm.data.name}>
            Adicionar
          </button>
        </div>
      </div>
    </div>
  );
}

function BodiesStep({
  members,
  parties,
  roles,
  parish,
}: {
  members: { executive: Member[]; assembly: Member[] };
  parties: Party[];
  roles: { executive: string[]; assembly: string[] };
  parish: Parish;
}) {
  const mandateForm = useForm({ mandate: parish.mandate ?? '' });
  const saveMandate = () => {
    mandateForm.transform((d) => ({ parish: d }));
    mandateForm.patch('/parish', {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => mandateForm.setDefaults(),
    });
  };
  useAutosave(mandateForm.isDirty, mandateForm.data, saveMandate);

  const initialMandate = (parish.mandate ?? '').match(/(\d{4})\D+(\d{4})/);
  const [startYear, setStartYear] = useState(initialMandate?.[1] ?? '');
  const [endYear, setEndYear] = useState(initialMandate?.[2] ?? '');
  const composeMandate = (s: string, e: string) => (s && e ? `${s}–${e}` : s || e || '');
  const pickStart = (y: string) => {
    setStartYear(y);
    mandateForm.setData('mandate', composeMandate(y, endYear));
  };
  const pickEnd = (y: string) => {
    setEndYear(y);
    mandateForm.setData('mandate', composeMandate(startYear, y));
  };
  const currentYear = new Date().getFullYear();
  const mandateYears = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

  return (
    <div className={card}>
      <StepHeader
        title="Órgão executivo e assembleia"
        help="Adicione os membros. A ordem define quem aparece em destaque (presidente primeiro)."
      />

      <div className="border-line mb-6 border-b pb-6">
        <Field label="Mandato" hint="Anos de início e fim do mandato em exercício.">
          <div className="grid max-w-xs grid-cols-2 gap-3">
            <SelectMenu
              value={startYear}
              options={mandateYears.map((y) => ({ value: String(y), label: String(y) }))}
              onChange={pickStart}
              placeholder="Início…"
              ariaLabel="Ano de início do mandato"
            />
            <SelectMenu
              value={endYear}
              options={mandateYears.map((y) => ({ value: String(y), label: String(y) }))}
              onChange={pickEnd}
              placeholder="Fim…"
              ariaLabel="Ano de fim do mandato"
            />
          </div>
        </Field>
        <AutosaveStatus
          processing={mandateForm.processing}
          saved={mandateForm.recentlySuccessful}
          failed={mandateForm.hasErrors}
        />
      </div>

      <BodyList
        body="executive"
        title="Órgão Executivo"
        list={members.executive}
        roleOptions={roles.executive}
        parties={parties}
      />
      <div className="mt-8">
        <BodyList
          body="assembly"
          title="Assembleia de Freguesia"
          list={members.assembly}
          roleOptions={roles.assembly}
          parties={parties}
        />
      </div>
    </div>
  );
}

// Party selector showing each party's logo + name (the admin picks one).
// Thin wrapper over the accessible SelectMenu, mapping parties → options.
function PartyPicker({
  value,
  parties,
  onChange,
}: {
  value: string;
  parties: Party[];
  onChange: (key: string) => void;
}) {
  return (
    <SelectMenu
      value={value}
      options={parties.map((p) => ({ value: p.key, label: p.label, iconUrl: p.logoUrl }))}
      onChange={onChange}
      placeholder="Afiliação (opcional)"
      ariaLabel="Afiliação partidária"
      noneLabel="Sem afiliação"
    />
  );
}

function PhotoCircle({
  src,
  fallback,
  onFile,
  title,
}: {
  src: string | null;
  fallback: ReactNode;
  onFile: (f: File) => void;
  title: string;
}) {
  return (
    <label
      className="border-line bg-background text-on-surface-light hover:border-primary relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border"
      title={title}
    >
      <input
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
        }}
      />
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : fallback}
    </label>
  );
}

function MemberPhotoControl({ member, onFile }: { member: Member; onFile: (f: File) => void }) {
  return (
    <PhotoCircle
      src={member.photoUrl ?? null}
      onFile={onFile}
      title="Alterar foto"
      fallback={
        <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold">
          {(member.name ?? '?').slice(0, 1).toUpperCase()}
        </span>
      }
    />
  );
}

function MemberRow({
  member,
  roleOptions,
  parties,
  onUp,
  onDown,
  canUp,
  canDown,
}: {
  member: Member;
  roleOptions: string[];
  parties: Party[];
  onUp: () => void;
  onDown: () => void;
  canUp: boolean;
  canDown: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const form = useForm({
    name: member.name ?? '',
    role: member.role ?? '',
    affiliation: member.affiliation ?? '',
  });
  const party = parties.find((p) => p.key === member.affiliation);
  const changePhoto = (file: File) =>
    router.patch(
      `/body-members/${member.id}`,
      { photo: file },
      { forceFormData: true, preserveScroll: true, preserveState: true },
    );
  const save = () => {
    form.transform((d) => ({ body_member: d }));
    form.patch(`/body-members/${member.id}`, {
      preserveScroll: true,
      onSuccess: () => setEditing(false),
    });
  };

  if (editing) {
    return (
      <div className="border-line bg-background rounded-md border p-3">
        <div className="flex items-start gap-2.5">
          <MemberPhotoControl member={member} onFile={changePhoto} />
          <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
            <input
              className={input}
              value={form.data.name}
              onChange={(e) => form.setData('name', e.target.value)}
              aria-label="Nome do membro"
            />
            <SelectMenu
              value={form.data.role}
              options={roleOptions.map((r) => ({ value: r, label: r }))}
              onChange={(v) => form.setData('role', v)}
              placeholder="Cargo…"
              ariaLabel="Cargo"
            />
            <PartyPicker
              value={form.data.affiliation}
              parties={parties}
              onChange={(key) => form.setData('affiliation', key)}
            />
          </div>
        </div>
        <div className="mt-3 flex gap-3">
          <button className={btnPrimary} onClick={save} disabled={!form.data.name}>
            Guardar
          </button>
          <button className={btnGhost} onClick={() => setEditing(false)}>
            Cancelar
          </button>
        </div>
        <FormErrors errors={form.errors} />
      </div>
    );
  }

  return (
    <div className="border-line flex items-center justify-between gap-2 rounded-md border px-3 py-2">
      <span className="flex min-w-0 items-center gap-2.5 text-sm">
        <ReorderButtons onUp={onUp} onDown={onDown} canUp={canUp} canDown={canDown} />
        <MemberPhotoControl member={member} onFile={changePhoto} />
        {party && <img src={party.logoUrl} alt="" className="h-5 w-5 shrink-0 rounded" />}
        <span className="min-w-0 truncate">
          <b>{member.name}</b> — {member.role}
          {party && <span className="text-on-surface-light"> · {party.label}</span>}
        </span>
      </span>
      <div className="flex shrink-0 gap-2">
        <RowButton icon="edit" onClick={() => setEditing(true)}>
          Editar
        </RowButton>
        <RowButton
          icon="delete"
          tone="danger"
          onClick={() => router.delete(`/body-members/${member.id}`, { preserveScroll: true })}
        >
          Remover
        </RowButton>
      </div>
    </div>
  );
}

function BodyList({
  body,
  title,
  list,
  roleOptions,
  parties,
}: {
  body: string;
  title: string;
  list: Member[];
  roleOptions: string[];
  parties: Party[];
}) {
  const form = useForm({
    body,
    name: '',
    role: '',
    affiliation: '',
    position: list.length,
    photo: null as File | null,
  });
  const add = () => {
    form.transform((d) => ({
      body_member: {
        body: d.body,
        name: d.name,
        role: d.role,
        affiliation: d.affiliation,
        position: d.position,
      },
      photo: d.photo,
    }));
    form.post('/body-members', {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => form.reset('name', 'role', 'affiliation', 'photo'),
    });
  };
  const newPhotoPreview = useMemo(
    () => (form.data.photo ? URL.createObjectURL(form.data.photo) : null),
    [form.data.photo],
  );
  useEffect(() => {
    return () => {
      if (newPhotoPreview) URL.revokeObjectURL(newPhotoPreview);
    };
  }, [newPhotoPreview]);

  const move = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= list.length) return;
    const ids = reorderIds(
      list.map((m) => m.id),
      index,
      to,
    );
    router.patch('/body-members/reorder', { ids }, { preserveScroll: true, preserveState: true });
  };

  return (
    <div>
      <h3 className="font-headline text-on-surface mb-3 text-base font-semibold">{title}</h3>
      <div className="mb-3 flex flex-col gap-2">
        {list.map((m, i) => (
          <MemberRow
            key={m.id}
            member={m}
            roleOptions={roleOptions}
            parties={parties}
            onUp={() => move(i, -1)}
            onDown={() => move(i, 1)}
            canUp={i > 0}
            canDown={i < list.length - 1}
          />
        ))}
      </div>
      <div className="flex items-start gap-2.5">
        <PhotoCircle
          src={newPhotoPreview}
          onFile={(f) => form.setData('photo', f)}
          title={newPhotoPreview ? 'Trocar foto' : 'Adicionar foto (opcional)'}
          fallback={<span className="material-symbols-outlined text-lg">photo_camera</span>}
        />
        <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
          <input
            className={input}
            placeholder="Nome"
            value={form.data.name}
            onChange={(e) => form.setData('name', e.target.value)}
            aria-label="Nome do membro"
          />
          <SelectMenu
            value={form.data.role}
            options={roleOptions.map((r) => ({ value: r, label: r }))}
            onChange={(v) => form.setData('role', v)}
            placeholder="Cargo…"
            ariaLabel="Cargo"
          />
          <PartyPicker
            value={form.data.affiliation}
            parties={parties}
            onChange={(key) => form.setData('affiliation', key)}
          />
        </div>
      </div>
      <button className={`${btnGhost} mt-3 block`} onClick={add} disabled={!form.data.name}>
        Adicionar membro
      </button>
      <FormErrors errors={form.errors} />
    </div>
  );
}

// Repeatable list of references ({ título, fonte, ligação }) for a history block.
function ReferencesEditor({
  value,
  onChange,
}: {
  value: Reference[];
  onChange: (refs: Reference[]) => void;
}) {
  const patch = (i: number, next: Partial<Reference>) =>
    onChange(value.map((r, idx) => (idx === i ? { ...r, ...next } : r)));
  return (
    <div>
      <span className={fieldLabel}>Referências</span>
      <p className="text-on-surface-light mb-2 text-xs">
        Fontes que sustentam este marco (documento, página web, arquivo). A ligação é opcional.
      </p>
      <div className="flex flex-col gap-3">
        {value.map((r, i) => (
          <div key={i} className="border-line flex flex-col gap-2 rounded-lg border p-3">
            <input
              className={input}
              placeholder="Título (ex.: Liber Fidei, doc. 630)"
              value={r.title}
              onChange={(e) => patch(i, { title: e.target.value })}
              aria-label="Título da referência"
            />
            <input
              className={input}
              placeholder="Fonte (ex.: Arquivo Distrital de Braga · 1079)"
              value={r.source}
              onChange={(e) => patch(i, { source: e.target.value })}
              aria-label="Fonte da referência"
            />
            <div className="flex items-center gap-2">
              <input
                className={input}
                placeholder="Ligação (opcional, https://…)"
                value={r.url}
                onChange={(e) => patch(i, { url: e.target.value })}
                aria-label="Ligação da referência"
              />
              <RowButton
                icon="delete"
                tone="danger"
                onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              >
                Remover
              </RowButton>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([...value, { title: '', source: '', url: '' }])}
        className={`${btnGhost} mt-2 inline-flex items-center gap-1.5`}
      >
        <span className="material-symbols-outlined text-[16px]">add</span>
        Adicionar referência
      </button>
    </div>
  );
}

function HistoryForm({
  entry,
  onDone,
  nextPosition = 0,
}: {
  entry?: HistoryEntry;
  onDone?: () => void;
  nextPosition?: number;
}) {
  const [references, setReferences] = useState<Reference[]>(entry?.references ?? []);
  const form = useForm({
    era: entry?.era ?? '',
    title: entry?.title ?? '',
    description: entry?.description ?? '',
    references: entry?.references ?? [],
    position: entry?.position ?? nextPosition,
  });
  const updateReferences = (refs: Reference[]) => {
    setReferences(refs);
    form.setData('references', refs);
  };
  const submit = () => {
    form.transform((d) => ({ history_entry: d }));
    if (entry) {
      form.patch(`/history-entries/${entry.id}`, { preserveScroll: true, onSuccess: onDone });
    } else {
      form.post('/history-entries', {
        preserveScroll: true,
        onSuccess: () => {
          form.reset();
          setReferences([]);
        },
      });
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr]">
        <div>
          <span className={fieldLabel}>Ano</span>
          <input
            className={input}
            placeholder="Ex.: 1079, Séc. XII"
            value={form.data.era}
            onChange={(e) => form.setData('era', e.target.value)}
            aria-label="Ano ou época"
          />
        </div>
        <div>
          <span className={fieldLabel}>Título</span>
          <input
            className={input}
            placeholder="Título do marco"
            value={form.data.title}
            onChange={(e) => form.setData('title', e.target.value)}
            aria-label="Título do marco"
          />
        </div>
      </div>
      <div>
        <span className={fieldLabel}>Descrição</span>
        <textarea
          className={input}
          rows={4}
          placeholder="O que aconteceu neste marco da história da freguesia…"
          value={form.data.description}
          onChange={(e) => form.setData('description', e.target.value)}
          aria-label="Descrição do marco"
        />
      </div>
      <ReferencesEditor value={references} onChange={updateReferences} />
      <div className="flex gap-3">
        <button
          className={btnPrimary}
          onClick={submit}
          disabled={form.processing || !form.data.title}
        >
          {entry ? 'Guardar' : 'Adicionar marco'}
        </button>
        {entry && (
          <button className={btnGhost} onClick={onDone}>
            Cancelar
          </button>
        )}
      </div>
      <FormErrors errors={form.errors} />
    </div>
  );
}

function HistoryRow({
  entry,
  onUp,
  onDown,
  canUp,
  canDown,
}: {
  entry: HistoryEntry;
  onUp: () => void;
  onDown: () => void;
  canUp: boolean;
  canDown: boolean;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <div className="border-line bg-background rounded-lg border p-4">
        <HistoryForm entry={entry} onDone={() => setEditing(false)} />
      </div>
    );
  }
  return (
    <div className="border-line flex items-start justify-between gap-3 rounded-lg border p-4">
      <div className="flex min-w-0 items-start gap-2">
        <ReorderButtons onUp={onUp} onDown={onDown} canUp={canUp} canDown={canDown} />
        <div className="min-w-0">
          {entry.era && (
            <div className="text-secondary text-xs font-bold uppercase">{entry.era}</div>
          )}
          <div className="text-on-surface text-sm font-semibold">{entry.title}</div>
          {entry.description && (
            <div className="text-on-surface-light mt-0.5 line-clamp-2 text-sm">
              {entry.description}
            </div>
          )}
          {entry.references.length > 0 && (
            <div className="text-on-surface-light mt-1 text-xs">
              {entry.references.length} referência{entry.references.length === 1 ? '' : 's'}
            </div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <RowButton icon="edit" onClick={() => setEditing(true)}>
          Editar
        </RowButton>
        <RowButton
          icon="delete"
          tone="danger"
          onClick={() => router.delete(`/history-entries/${entry.id}`, { preserveScroll: true })}
        >
          Remover
        </RowButton>
      </div>
    </div>
  );
}

function HistoryList({ entries }: { entries: HistoryEntry[] }) {
  const move = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= entries.length) return;
    const ids = reorderIds(
      entries.map((e) => e.id),
      index,
      to,
    );
    router.patch(
      '/history-entries/reorder',
      { ids },
      { preserveScroll: true, preserveState: true },
    );
  };
  return (
    <div className={card}>
      <div className="mb-6 flex flex-col gap-3">
        {entries.map((e, i) => (
          <HistoryRow
            key={e.id}
            entry={e}
            onUp={() => move(i, -1)}
            onDown={() => move(i, 1)}
            canUp={i > 0}
            canDown={i < entries.length - 1}
          />
        ))}
        {entries.length === 0 && (
          <p className="text-on-surface-light text-sm">Ainda não há marcos históricos.</p>
        )}
      </div>
      <div className="border-line border-t pt-5">
        <HistoryForm nextPosition={entries.length} />
      </div>
    </div>
  );
}

function ContentStep({ historyEntries }: { historyEntries: HistoryEntry[] }) {
  return (
    <div className="flex flex-col gap-6">
      <StepHeader
        title="História"
        help="Construa a cronologia da freguesia — cada marco com ano, título, descrição e referências. A ordem em que os adiciona é a ordem em que aparecem. As restantes páginas institucionais são geradas automaticamente a partir dos dados que configurou."
      />
      <HistoryList entries={historyEntries} />
    </div>
  );
}

function AssistantStep({ parish }: { parish: Parish }) {
  const form = useForm({ chat_enabled: parish.chatEnabled });
  const save = () => {
    form.transform((d) => ({ parish: d }));
    form.patch('/parish', {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => form.setDefaults(),
    });
  };
  useAutosave(form.isDirty, form.data, save);
  return (
    <div className={card}>
      <StepHeader
        title="Assistente virtual"
        help="Um assistente de chat que responde a dúvidas dos visitantes, com base nos dados que configurou (balcões, horários, serviços)."
      />
      <div className="border-line flex items-center justify-between rounded-lg border p-3">
        <div>
          <div className="text-sm font-semibold">Ativar assistente virtual</div>
          <div className="text-on-surface-light text-xs">Requer chave da API Mistral</div>
        </div>
        <Switch
          checked={form.data.chat_enabled}
          onChange={(v) => form.setData('chat_enabled', v)}
          label="Ativar assistente virtual"
        />
      </div>
      <div className="mt-4">
        <AutosaveStatus
          processing={form.processing}
          saved={form.recentlySuccessful}
          failed={form.hasErrors}
        />
      </div>
    </div>
  );
}

function SummaryCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-line bg-background rounded-lg border p-4">
      <div className="text-secondary mb-2 text-[11px] font-bold tracking-widest uppercase">
        {title}
      </div>
      <div className="text-on-surface text-sm">{children}</div>
    </div>
  );
}

function BrandPreview({ parish }: { parish: Parish }) {
  return (
    <div
      className="border-line overflow-hidden rounded-xl border"
      style={
        {
          '--color-primary': parish.colorPrimary,
          '--color-secondary': parish.colorSecondary,
          '--color-tertiary': parish.colorTertiary,
        } as React.CSSProperties
      }
    >
      <div className="bg-on-surface flex items-center gap-3 px-4 py-3 text-white">
        {parish.logoUrl && <img src={parish.logoUrl} alt="" className="h-8 w-auto" />}
        <div>
          <div className="text-[10px] font-bold tracking-widest text-white/70 uppercase">
            {parish.parishType === 'uniao' ? 'União das Freguesias' : 'Freguesia'}
          </div>
          <div className="font-headline text-sm">{parish.shortName || 'A sua freguesia'}</div>
        </div>
      </div>
      <div
        className="h-1"
        style={{
          background:
            'linear-gradient(90deg, var(--color-primary) 0 33.33%, var(--color-secondary) 33.33% 66.66%, var(--color-tertiary) 66.66% 100%)',
        }}
      />
      <div className="bg-background flex items-center gap-3 p-4">
        <span
          className="inline-block rounded-md px-3 py-1.5 text-xs font-semibold text-white"
          style={{ background: 'var(--color-primary)' }}
        >
          Pedir atestado
        </span>
        <span className="text-xs font-semibold underline" style={{ color: 'var(--color-primary)' }}>
          Ver eventos
        </span>
      </div>
    </div>
  );
}

function ReviewStep({
  parish,
  locations,
  services,
  bodyMembers,
  historyEntries,
}: {
  parish: Parish;
  locations: Location[];
  services: Service[];
  bodyMembers: { executive: Member[]; assembly: Member[] };
  historyEntries: HistoryEntry[];
}) {
  const enabledServices = services.filter((s) => s.enabled);
  const dash = (v: string | null | undefined) => (v && v.trim() ? v : '—');

  return (
    <div className={card}>
      <StepHeader
        title={parish.published ? 'O site está publicado' : 'Quase pronto'}
        help="Reveja as opções escolhidas. Os itens obrigatórios têm de estar concluídos para publicar; os opcionais podem ser adicionados depois."
      />

      <div className="mb-5">
        <div className="text-on-surface-light mb-2 text-[11px] font-bold tracking-widest uppercase">
          Aspeto do site
        </div>
        <BrandPreview parish={parish} />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryCard title="Identidade">
          <div className="font-semibold">{dash(parish.officialName)}</div>
          <div className="text-on-surface-light text-xs">
            {parish.parishType === 'uniao' ? 'União de Freguesias' : 'Freguesia'} ·{' '}
            {dash(parish.district)}
          </div>
          <div className="text-on-surface-light text-xs">{dash(parish.contactEmail)}</div>
        </SummaryCard>

        <SummaryCard title="Marca">
          <div className="flex items-center gap-2">
            {[parish.colorPrimary, parish.colorSecondary, parish.colorTertiary].map((c) => (
              <span
                key={c}
                className="border-line h-5 w-5 rounded"
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
            <span className="text-on-surface-light text-xs">
              {parish.logoUrl ? 'Logótipo carregado' : 'Sem logótipo'}
            </span>
          </div>
        </SummaryCard>

        <SummaryCard title={`Localizações (${locations.length})`}>
          {locations.length
            ? locations.map((l) => l.name).join(' · ')
            : 'Nenhuma localização ainda.'}
        </SummaryCard>

        <SummaryCard title={`Serviços ativos (${enabledServices.length})`}>
          {enabledServices.length
            ? enabledServices.map((s) => `${s.icon} ${s.title}`).join(' · ')
            : 'Nenhum serviço ativo.'}
        </SummaryCard>

        <SummaryCard title="Órgãos">
          Executivo: {bodyMembers.executive.length} · Assembleia: {bodyMembers.assembly.length}
        </SummaryCard>

        <SummaryCard title="Conteúdo & assistente">
          {historyEntries.length} marco(s) de história · Assistente{' '}
          {parish.chatEnabled ? 'ativo' : 'inativo'}
        </SummaryCard>
      </div>

      <div className="text-on-surface-light mb-2 text-[11px] font-bold tracking-widest uppercase">
        Requisitos
      </div>
      <div className="mb-6 flex flex-col">
        {parish.checklist.map((c) => (
          <div
            key={c.key}
            className="border-line flex items-center gap-3 border-b py-3 last:border-0"
          >
            <span
              className={`flex h-6 w-6 flex-none items-center justify-center rounded-full ${
                c.done ? 'bg-tertiary text-white' : 'border-line bg-background border'
              }`}
            >
              {c.done && <span className="material-symbols-outlined text-[16px]">check</span>}
            </span>
            <span className="text-sm font-medium">{c.label}</span>
          </div>
        ))}
      </div>

      {parish.published ? (
        <div className="border-line rounded-xl border p-5">
          <b className="font-headline text-base">O site está online.</b>
          <p className="text-on-surface-light mt-1 mb-4 text-sm">
            Pode voltar a ocultá-lo do público a qualquer momento.
          </p>
          <button className={btnGhost} onClick={() => router.patch('/parish/unpublish')}>
            Ocultar site
          </button>
        </div>
      ) : (
        <div className="border-tertiary/30 bg-tertiary/[0.06] rounded-xl border p-5">
          <b className="font-headline text-base">Publicar o site</b>
          <p className="text-on-surface-light mt-1 mb-4 text-sm">
            Ao publicar, o site público fica imediatamente visível para todos.
          </p>
          <button
            className={btnPrimary}
            disabled={!parish.readyToPublish}
            onClick={() => router.patch('/parish/publish')}
          >
            {parish.readyToPublish ? 'Publicar site' : 'Conclua os itens obrigatórios'}
          </button>
        </div>
      )}
    </div>
  );
}
